/*********************************************************************
 * System Polling Manager.
 * Allows registration of multiple callbacks at different
 * intervals. Supports pausing and resuming individual callbacks.
 ********************************************************************/

type Callback = () => void;

interface RegisteredCallback {
    callback: Callback;
    intervalMS: number; // Individual callback polling interval in ms
    lastFired: number; // Timestamp of last invocation
    paused: boolean; // Whether the callback is currently paused
}

export function createPollingManager() {
    let callbacks: RegisteredCallback[] = [];
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const tickIntervalMs = 300; // Frequency to check callbacks (adjust as needed)

    function registerCallback(callback: Callback, intervalMS: number) {
        if (intervalMS <= 0) {
            throw new Error('intervalMS must be greater than 0');
        }
        const registeredCallback: RegisteredCallback = {
            callback,
            intervalMS,
            lastFired: 0,
            paused: false,
        };
        callbacks.push(registeredCallback);

        // Return control functions for this callback
        return {
            pause: () => {
                registeredCallback.paused = true;
            },
            resume: () => {
                registeredCallback.paused = false;
            },
            unregister: () => {
                callbacks = callbacks.filter((c) => c !== registeredCallback);
            },
        };
    }

    function start() {
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(() => {
            const now = Date.now();
            callbacks.forEach((c) => {
                if (!c.paused && now - c.lastFired >= c.intervalMS) {
                    c.callback();
                    c.lastFired = now;
                }
            });
        }, tickIntervalMs);
    }

    function stop() {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    }

    return {
        registerCallback,
        start,
        stop,
    };
}

/*** Vue Composition API hook to access the Polling Manager */
import { getCurrentInstance } from 'vue';

export function usePollingManager() {
    const instance = getCurrentInstance();
    if (!instance) {
        throw new Error('usePollingManager must be called within setup()');
    }
    return instance.appContext.config.globalProperties.$pollingManager;
}
